import axios from "axios";

import { API_URLS } from "src/utils/API_URLS";

import { defaultAllocationLabels } from "../model/defaults";
import {
  CreatorFallbackRule,
  MappingKey,
  MarketTarget,
  ReportConfiguration,
  ReportingDataset,
  ReportingField,
  ReportingRecord,
  SnapshotStatus,
} from "../model/types";

/**
 * The single seam between this module and its data.
 *
 * The backend keeps a cached projection of the workspace's creator-task rows and serves it
 * stale-while-revalidate: this call always returns immediately from that snapshot, and the
 * server rebuilds behind the response when the snapshot has aged out. Nothing here ever
 * waits on a recomputation, which is why the module can ship whole rows and still do all
 * its aggregation in the browser.
 */
/** Thrown when the workspace is not allowed to use the module, rather than a real failure. */
export class ReportingUnavailableError extends Error {}

export interface ReportingDatasetStatus {
  status: SnapshotStatus;
  generatedAt: string | null;
  rowCount: number;
}

export async function loadReportingDataset(
  workspaceUuid: string,
): Promise<ReportingDataset> {
  try {
    const { data } = await axios.get(
      API_URLS.getFinanceReportingDataset(workspaceUuid),
    );

    return normaliseDataset(data?.content ?? data ?? {});
  } catch (error: any) {
    // The API is the authority on both the workspace allowlist (404) and the
    // FINANCE_REPORTING permission (401). Neither is an error worth telling the user to
    // retry, so they are separated from an actual load failure.
    const status = error?.response?.status;

    if (status === 404 || status === 401) {
      throw new ReportingUnavailableError();
    }

    throw error;
  }
}

/** Polls only snapshot metadata; use `loadReportingDataset` after the version changes. */
export async function loadReportingDatasetStatus(
  workspaceUuid: string,
): Promise<ReportingDatasetStatus> {
  try {
    const { data } = await axios.get(
      API_URLS.getFinanceReportingDatasetStatus(workspaceUuid),
    );
    const payload = data?.content ?? data ?? {};

    return {
      status: (payload.status as SnapshotStatus) ?? "building",
      generatedAt: payload.generatedAt ?? null,
      rowCount: payload.rowCount ?? 0,
    };
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 404 || status === 401) throw new ReportingUnavailableError();

    throw error;
  }
}

/** Asks the server to rebuild the snapshot. Returns once the rebuild is queued. */
export async function refreshReportingDataset(
  workspaceUuid: string,
): Promise<void> {
  await axios.post(API_URLS.refreshFinanceReportingDataset(workspaceUuid));
}

/**
 * Persists the admin configuration onto the workspace.
 *
 * Only called from an explicit user action - the module deliberately does not autosave, so
 * a mapping change can be checked against the preview before it becomes the definition
 * every viewer of this workspace sees.
 */
export async function saveReportingConfiguration(
  workspaceUuid: string,
  configuration: ReportConfiguration,
): Promise<void> {
  await axios.put(API_URLS.financeReportingConfig(workspaceUuid), {
    mappings: configuration.mappings,
    statusMode: configuration.statusMode,
    includedStatuses: configuration.includedStatuses,
    reportName: configuration.reportName,
    description: configuration.description,
    allocationLabels: configuration.allocationLabels,
    disableFrontendRefreshAction: configuration.disableFrontendRefreshAction,
    additionalDimensions: configuration.additionalDimensions,
    roleLabels: configuration.roleLabels,
    reportingStartMonth: configuration.reportingStartMonth,
    dataQualityEnabled: configuration.dataQualityEnabled,
    creatorFallbackRules: configuration.creatorFallbackRules,
    targets: configuration.targets,
  });
}

const MAPPING_KEYS: MappingKey[] = [
  "revenue",
  "cost",
  "date",
  "budgetMonth",
  "status",
  "client",
  "manager",
  "campaign",
  "campaignName",
  "creator",
  "creatorName",
  "market",
  "costType",
];

const YEAR_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

function normaliseDataset(payload: Record<string, any>): ReportingDataset {
  const config = payload.config ?? {};
  const mappings = {} as Record<MappingKey, string>;

  MAPPING_KEYS.forEach((key) => {
    const value = config.mappings?.[key];
    mappings[key] = typeof value === "string" ? value : "";
  });

  return {
    status: (payload.status as SnapshotStatus) ?? "error",
    fields: Array.isArray(payload.fields)
      ? (payload.fields as ReportingField[])
      : [],
    records: Array.isArray(payload.rows)
      ? (payload.rows as ReportingRecord[])
      : [],
    generatedAt: payload.generatedAt ?? null,
    rowCount: payload.rowCount ?? 0,
    isStale: Boolean(payload.isStale),
    error: payload.error ?? null,
    configuration: {
      reportName: config.reportName ?? "Financial performance",
      description: config.description ?? "",
      mappings,
      statusMode: config.statusMode === "selected" ? "selected" : "all",
      includedStatuses: Array.isArray(config.includedStatuses)
        ? config.includedStatuses
        : [],
      allocationLabels: {
        reportingDate:
          typeof config.allocationLabels?.reportingDate === "string" &&
          config.allocationLabels.reportingDate.trim() !== ""
            ? config.allocationLabels.reportingDate
            : defaultAllocationLabels.reportingDate,
        budgetMonth:
          typeof config.allocationLabels?.budgetMonth === "string" &&
          config.allocationLabels.budgetMonth.trim() !== ""
            ? config.allocationLabels.budgetMonth
            : defaultAllocationLabels.budgetMonth,
      },
      disableFrontendRefreshAction: Boolean(
        config.disableFrontendRefreshAction,
      ),
      additionalDimensions: Array.isArray(config.additionalDimensions)
        ? config.additionalDimensions
            .filter(
              (dimension: any) =>
                (dimension.key === "customDimension1" ||
                  dimension.key === "customDimension2") &&
                typeof dimension.label === "string" &&
                Array.isArray(dimension.fieldUuids),
            )
            .map((dimension: any) => ({
              key: dimension.key,
              label: dimension.label,
              fieldUuids: dimension.fieldUuids.filter(
                (uuid: unknown) => typeof uuid === "string",
              ),
            }))
        : [],
      roleLabels:
        config.roleLabels && typeof config.roleLabels === "object"
          ? Object.fromEntries(
              MAPPING_KEYS.filter(
                (key) => typeof config.roleLabels[key] === "string",
              ).map((key) => [key, config.roleLabels[key]]),
            )
          : {},
      reportingStartMonth:
        typeof config.reportingStartMonth === "string" &&
        YEAR_MONTH_PATTERN.test(config.reportingStartMonth)
          ? config.reportingStartMonth
          : null,
      dataQualityEnabled: config.dataQualityEnabled !== false,
      creatorFallbackRules: Array.isArray(config.creatorFallbackRules)
        ? config.creatorFallbackRules
            .filter(
              (rule: any) =>
                typeof rule.costTypeValue === "string" &&
                rule.costTypeValue.trim() !== "" &&
                typeof rule.label === "string" &&
                rule.label.trim() !== "",
            )
            .map(
              (rule: any): CreatorFallbackRule => ({
                costTypeValue: rule.costTypeValue,
                label: rule.label,
              }),
            )
        : [],
      targets: Array.isArray(config.targets)
        ? config.targets
            .filter(
              (target: any) =>
                typeof target.market === "string" &&
                target.market.trim() !== "" &&
                typeof target.month === "string" &&
                YEAR_MONTH_PATTERN.test(target.month),
            )
            .map(
              (target: any): MarketTarget => ({
                market: target.market,
                month: target.month,
                revenue:
                  typeof target.revenue === "number" ? target.revenue : null,
                profit:
                  typeof target.profit === "number" ? target.profit : null,
              }),
            )
        : [],
    },
    isStored: Boolean(config.isStored),
    savedAt: config.savedAt ?? null,
    unmappedRoles: Array.isArray(config.unmappedRoles)
      ? (config.unmappedRoles as MappingKey[])
      : [],
  };
}
